import React from 'react';
import { Card, Row, Col, Form, Select, Input, Alert } from 'antd';

const { Option } = Select;

const parseMysqlDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.includes('T')) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parts = raw.split(/[- :]/).filter(Boolean);
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  const hh = parts.length >= 4 ? Number(parts[3]) : 0;
  const mm = parts.length >= 5 ? Number(parts[4]) : 0;
  const ss = parts.length >= 6 ? Number(parts[5]) : 0;
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, hh, mm, ss);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getCsdVigenciaLabel = (cert) => {
  const now = new Date();
  const exp = parseMysqlDateTime(cert?.expiration_date);
  if (!exp) return 'Sin fecha';
  const daysLeft = Math.ceil((exp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
  const dateText = exp.toLocaleDateString('es-MX');
  if (exp.getTime() < now.getTime()) return `Vencido (${dateText})`;
  if (daysLeft <= 30) return `Por vencer (${dateText})`;
  return `Vigente (${dateText})`;
};

const formatSerieFolio = (serie, folio) => {
  const s = serie || '';
  const f = folio == null ? '' : String(folio).padStart(6, '0');
  if (!s && !f) return '';
  if (!s) return f;
  if (!f) return s;
  return `${s}-${f}`;
};

export default function CompanyInfoSection({
  companies,
  certificates,
  folioSeries,
  selectedCompany,
  existingInvoiceDate,
  getDateOptions,
  handleCompanySelect,
  handleCertificateSelect,
  certificateAlert,
  disableSeries,
  loadedSerie,
  loadedFolio
}) {
  return (
    <Card title="Información de la Empresa" size="small" style={{ marginBottom: '12px' }} styles={{ body: { padding: '12px' } }}>
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            name="company_id"
            style={{ marginBottom: '8px' }}
            rules={[{ required: true, message: 'Seleccione una empresa' }]}
          ><Select
              data-testid="company-select"
              aria-label="Empresa"
              size="small"
              placeholder="Empresa"
              onChange={handleCompanySelect}
            >
              {companies.map(company => (
                <Option key={company.id} value={company.id}>
                  {company.tax_name} - {company.tax_id}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Certificado */}
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            name="certificate_id"
            style={{ marginBottom: '8px' }}
            rules={[{ required: true, message: 'Seleccione un certificado' }]}
          >
            <Select
              data-testid="certificate-select"
              aria-label="Certificado Digital"
              size="small"
              placeholder="Certificado Digital"
              disabled={!selectedCompany}
              notFoundContent={certificates.length === 0 ? "No hay certificados disponibles" : "Seleccione un certificado"}
              onChange={handleCertificateSelect}
            >
              {certificates.map(cert => (
                <Option key={cert.id} value={cert.id}>
                  {cert.certificate_number} - {getCsdVigenciaLabel(cert)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {certificateAlert ? (
            <Alert
              type={certificateAlert.type}
              showIcon
              message={certificateAlert.message}
              style={{ marginTop: 4 }}
            />
          ) : null}
        </Col>

        <Col xs={24} sm={12} md={3}>
          {disableSeries ? (
            <>
              {/* Mantener el valor de folio_serie_id para validación/envío */}
              <Form.Item name="folio_serie_id" style={{ display: 'none' }}>
                <Input type="hidden" />
              </Form.Item>
              {/* Mostrar serie y folio como lectura */}
              <Form.Item style={{ marginBottom: '8px' }}>
                <Input
                  size="small"
                  disabled
                  value={formatSerieFolio(loadedSerie, loadedFolio)}
                  placeholder="Serie"
                />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="folio_serie_id"
              style={{ marginBottom: '8px' }}
              rules={[{ required: true, message: 'Seleccione una serie' }]}
            >
              <Select 
                data-testid="serie-select"
                aria-label="Serie"
                size="small"
                placeholder="Serie"
                disabled={!selectedCompany}
              >
                {folioSeries.map(fs => (
                  <Option key={fs.id} value={fs.id}>
                    {formatSerieFolio(fs.serie, fs.folio)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>
        {/* Fecha */}
        <Col xs={24} sm={12} md={3}>
          <Form.Item
            name="fecha"
            style={{ marginBottom: '8px' }}
            rules={[{ required: true, message: 'Seleccione una fecha' }]}
          >
            <Select
              data-testid="date-select"
              aria-label="Fecha"
              size="small"
              placeholder="Fecha"
              disabled={!!existingInvoiceDate}
            >
              {getDateOptions().map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
}
